import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MessageService } from '../local/message.service';
import { ConfigService } from '../local/config.service';
import { catchError, map } from 'rxjs/operators';
import { IBaseObject } from '../../_models/BaseObject';

export abstract class BaseHubService<T extends IBaseObject> {
  protected readonly baseUrl: string;
  protected readonly baseHubUrl: string;
  protected isInitialized = false;

  public loading = false;
  public readonly state$ = new BehaviorSubject<string>('NotConnected');
  public readonly connectionWithError$ = new BehaviorSubject<string | undefined>(undefined);
  public readonly connection: HubConnection;

  constructor(
    endpoint: string,
    hubEndpoint: string,
    protected readonly label: string,
    protected http: HttpClient,
    protected readonly messageService: MessageService,
    protected readonly configService: ConfigService
  ) {
    this.baseUrl = `${this.configService.Config.apiUrl}/${endpoint}`;
    this.baseHubUrl = `${this.configService.Config.apiUrl}/${hubEndpoint}`;

    const connectionProperties = this.configService.Config.shouldUseWebSocketsCommunicationForHubs
      ? {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,          
        }
      : {
          transport: HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
        };

    this.connection = new HubConnectionBuilder()
      .withUrl(this.baseHubUrl, connectionProperties)
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();
    this.connection.serverTimeoutInMilliseconds = 120000;

    this.connection.onreconnecting((error) => {
      this.updateState(error);
    });
    this.connection.onreconnected((connectionId) => {
      this.updateState(undefined);
    });
    this.connection.onclose((error) => {
      this.updateState(error);
    });

    this.connection.on('actualisation', (data: IBaseObject[]) => {
      console.debug(`[HUB][${this.label}] actualisation`, data);
      this.dispatchUpdateData(data);
    });
  }

  protected async start(): Promise<void> {
    try {
      if (
        this.connection &&
        (this.connection.state === HubConnectionState.Connected || this.connection.state === HubConnectionState.Connecting)
      ) {
        return;
      }
      await this.connection.start();
      this.updateState(undefined);
    } catch (e: any) {
      this.updateState(e);
    } finally {
      this.isInitialized = true;
    }
  }

  protected async stop(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
    }
  }

  protected handleError(error: HttpErrorResponse): Observable<undefined> {
    // if (error.status >= 500) {
    //   this.messageService.error(`${this.label} : ${error.statusText}`);
    // } else {
    //   switch (error.status) {
    //     case 404:
    //       this.messageService.info(`${this.label} introuvable`);
    //       break;
    //     default:
    //       if (error.error) {
    //         this.messageService.error(error.error.title);
    //       } else {
    //         this.messageService.error(error.message);
    //       }
    //       break;
    //   }
    // }

    return of(undefined);
  }

  private updateState(error: Error | undefined): void {
    this.state$.next(this.connection.state);
    this.connectionWithError$.next(error?.message);
  }

  public hasChanges(): boolean {
    return true;
  }

  public all(): Observable<T[]> {
    return this.http.get<T[]>(this.baseUrl).pipe(
      map((x) => {
        return x.filter((v) => v.id != null);
      })
    );
  }

  public retrieve(id: number | string): Observable<T | undefined> {
    return this.http.get<T | undefined>(this.baseUrl + `/${id}`).pipe(catchError((error) => this.handleError(error)));
  }

  public create(sp: T): Observable<void | undefined> {
    return this.http.post<void>(this.baseUrl + '/creation', sp).pipe(catchError((error) => this.handleError(error)));
  }

  public update(sp: T): Observable<void | undefined> {
    if (!sp.id) {
      throw new Error(`L'identifiant id de l'élément est introuvable.`);
    }

    return this.http.post<void>(this.baseUrl + '/edition', sp).pipe(catchError((error) => this.handleError(error)));
  }

  public delete(id: number | string): Observable<void | undefined> {
    return this.http.delete<void>(this.baseUrl + `/suppression?id=${id}`).pipe(catchError((error) => this.handleError(error)));
  }

  public async sendMessageToHub(methodName: 'creation' | 'edition' | 'suppression', message: string): Promise<void> {
    try {
      await this.connection.send(methodName, { message });
    } catch (error) {
      console.error(error);
    }
  }

  protected abstract dispatchUpdateData(data: IBaseObject[]): void;
}
